/**
 * Film Schedule Calendar Component
 * Modern TV guide with prime time focus
 */

import { useState, useEffect, useRef, useCallback } from '@wordpress/element';
import { useFilms } from '../context/FilmContext';
import { useReminder } from '../context/ReminderContext';
import dayjs from 'dayjs';

export default function FilmSchedule() {
    const { fetchWeekSchedule, loading, error } = useFilms();
    const { openReminder } = useReminder();
    const [weekData, setWeekData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [weekStart, setWeekStart] = useState(dayjs().format('YYYY-MM-DD'));
    const [highlightTime, setHighlightTime] = useState(null);
    const [showEarlier, setShowEarlier] = useState(false);
    const [trailerModal, setTrailerModal] = useState(null);
    const highlightRef = useRef(null);
    const nowPlayingRef = useRef(null);
    const earlierStripRef = useRef(null);
    const daysScrollRef = useRef(null);
    const autoLoadTimer = useRef(null);
    const hasInitialized = useRef(false);
    const userHasScrolled = useRef(false);

    const t = window.rialtoData?.translations || {};
    const PRIME_TIME_START = 18; // 6pm

    // Check if a screening is "Now Playing" (currently airing)
    const isNowPlaying = (screening, date) => {
        const today = dayjs().format('YYYY-MM-DD');
        if (date !== today) return false;

        const now = dayjs();
        const [hours, minutes] = screening.time.split(':').map(Number);
        const screeningStart = dayjs().hour(hours).minute(minutes).second(0);
        const duration = screening.duration || 120; // default 2 hours
        const screeningEnd = screeningStart.add(duration, 'minute');

        return now.isAfter(screeningStart) && now.isBefore(screeningEnd);
    };

    // Find the "Now Playing" screening index for scrolling
    const findNowPlayingIndex = (screenings, date) => {
        if (!screenings) return -1;
        return screenings.findIndex(s => isNowPlaying(s, date));
    };

    // Parse URL parameters on mount
    useEffect(() => {
        if (hasInitialized.current) return;

        const urlParams = new URLSearchParams(window.location.search);
        const dayParam = urlParams.get('day');
        const highlightParam = urlParams.get('highlight');

        if (dayParam) {
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const targetDayIndex = dayNames.indexOf(dayParam.toLowerCase());

            if (targetDayIndex !== -1) {
                const today = dayjs();
                const currentDayIndex = today.day();
                let daysToAdd = targetDayIndex - currentDayIndex;

                if (daysToAdd < 0) {
                    daysToAdd += 7;
                }

                const targetDate = today.add(daysToAdd, 'day');
                const targetDateStr = targetDate.format('YYYY-MM-DD');
                const weekStartDate = targetDate.startOf('week').add(1, 'day');
                setWeekStart(weekStartDate.format('YYYY-MM-DD'));
                setSelectedDate(targetDateStr);
            }
        }

        if (highlightParam) {
            setHighlightTime(highlightParam);
        }

        hasInitialized.current = true;
    }, []);

    // Scroll to highlighted screening after data loads
    useEffect(() => {
        if (highlightRef.current && highlightTime) {
            setTimeout(() => {
                highlightRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 300);
        }
    }, [weekData, selectedDate, highlightTime]);

    // Auto-scroll earlier strip to "Now Playing" item on mobile
    useEffect(() => {
        if (earlierStripRef.current && nowPlayingRef.current) {
            setTimeout(() => {
                const strip = earlierStripRef.current;
                const nowPlayingEl = nowPlayingRef.current;
                if (strip && nowPlayingEl) {
                    const stripRect = strip.getBoundingClientRect();
                    const itemRect = nowPlayingEl.getBoundingClientRect();
                    // Scroll to center the now playing item
                    const scrollLeft = nowPlayingEl.offsetLeft - (stripRect.width / 2) + (itemRect.width / 2);
                    strip.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
                }
            }, 400);
        }
    }, [weekData, selectedDate]);

    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && trailerModal) {
                setTrailerModal(null);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [trailerModal]);

    // Auto-load next/previous week when scrolling to edge of days strip
    useEffect(() => {
        const el = daysScrollRef.current;
        if (!el) return;

        userHasScrolled.current = false;

        const handleTouchStart = () => { userHasScrolled.current = true; };

        const handleScroll = () => {
            if (!userHasScrolled.current) return;
            clearTimeout(autoLoadTimer.current);
            autoLoadTimer.current = setTimeout(() => {
                const { scrollLeft, scrollWidth, clientWidth } = el;
                if (scrollWidth <= clientWidth) return; // All days visible, no auto-load
                if (scrollLeft + clientWidth >= scrollWidth - 5) {
                    navigateWeek(1);
                }
            }, 400);
        };

        el.addEventListener('touchstart', handleTouchStart, { passive: true });
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            el.removeEventListener('touchstart', handleTouchStart);
            el.removeEventListener('scroll', handleScroll);
            clearTimeout(autoLoadTimer.current);
        };
    }, [weekStart]);

    // Scroll the days strip back to the start when a new week loads
    useEffect(() => {
        if (daysScrollRef.current && weekData) {
            daysScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        }
    }, [weekData]);

    useEffect(() => {
        loadWeek(weekStart);
    }, [weekStart]);

    const loadWeek = async (startDate) => {
        const data = await fetchWeekSchedule(startDate);
        setWeekData(data);

        if (!hasInitialized.current && data.days && data.days.length > 0) {
            const firstDayWithScreenings = data.days.find(d => d.screenings?.length > 0);
            if (firstDayWithScreenings) {
                setSelectedDate(firstDayWithScreenings.date);
            }
        }
    };

    // Convert 24hr time to 12hr with am/pm - returns object for separate styling
    const formatTime12hr = (time24) => {
        const [hours, minutes] = time24.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        return {
            time: `${hours12}:${String(minutes).padStart(2, '0')}`,
            period: period
        };
    };

    // Short time format for strip
    const formatTimeShort = (time24) => {
        const [hours, minutes] = time24.split(':').map(Number);
        const period = hours >= 12 ? 'pm' : 'am';
        const hours12 = hours % 12 || 12;
        return `${hours12}:${String(minutes).padStart(2, '0')}${period}`;
    };

    // Get hour from time string (24hr format "HH:MM")
    const getHour = (time24) => {
        return parseInt(time24.split(':')[0], 10);
    };

    // Check if a time matches the highlight time
    const isHighlightedTime = (time24) => {
        if (!highlightTime) return false;
        const normalized = time24.replace(':', '');
        return normalized === highlightTime;
    };

    const navigateWeek = (direction) => {
        const newStart = dayjs(weekStart).add(direction * 7, 'day').format('YYYY-MM-DD');
        setWeekStart(newStart);
        // Also select the first day of the new week
        setSelectedDate(newStart);
    };

    const getSelectedDay = () => {
        if (!weekData?.days) return null;
        return weekData.days.find(d => d.date === selectedDate);
    };

    const formatDayLabel = (date) => {
        const d = dayjs(date);
        const today = dayjs().format('YYYY-MM-DD');
        const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

        if (date === today) return t.today || 'TODAY';
        if (date === tomorrow) return t.tomorrow || 'TOMORROW';
        return d.format('ddd').toUpperCase();
    };

    // Get earlier screenings (before 6pm)
    const getEarlierScreenings = (screenings) => {
        if (!screenings) return [];
        return screenings.filter(s => getHour(s.time) < PRIME_TIME_START);
    };

    // Get prime time screenings (6pm and later)
    const getPrimeTimeScreenings = (screenings) => {
        if (!screenings) return [];
        return screenings.filter(s => getHour(s.time) >= PRIME_TIME_START);
    };

    // Handle row click
    const handleRowClick = (url, e) => {
        // Don't navigate if clicking on action buttons or ratings
        if (e.target.closest('.schedule-actions') || e.target.closest('.schedule-ratings')) return;
        window.location.href = url;
    };

    // Handle remind me click
    const handleRemindMe = (e, screening) => {
        e.stopPropagation();
        const dt = dayjs(`${selectedDate} ${screening.time}`);
        if (dt.isBefore(dayjs())) return; // Don't open for past screenings
        const formatted = dt.format('ddd, D MMM, h:mm a');
        openReminder({
            screeningId: screening.screening_id,
            filmTitle: screening.film.title,
            screeningTime: formatted,
            screeningDatetime: dt.toISOString(),
        });
    };

    // Handle trailer click - open modal
    const handleTrailer = (e, film) => {
        e.stopPropagation();
        if (film.trailer_embed) {
            setTrailerModal({
                title: film.title,
                embedUrl: film.trailer_embed + '?autoplay=1&rel=0'
            });
        } else if (film.trailer_url) {
            // Extract video ID and create embed URL
            const match = film.trailer_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
            if (match) {
                setTrailerModal({
                    title: film.title,
                    embedUrl: `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`
                });
            }
        }
    };

    // Close trailer modal
    const closeTrailerModal = () => {
        setTrailerModal(null);
    };

    if (loading && !weekData) {
        return (
            <div className="rialto-loading">
                <div className="rialto-spinner"></div>
            </div>
        );
    }

    if (error) {
        return <div className="rialto-error">{error}</div>;
    }

    const selectedDay = getSelectedDay();
    const earlierScreenings = getEarlierScreenings(selectedDay?.screenings);
    const primeTimeScreenings = getPrimeTimeScreenings(selectedDay?.screenings);
    const displayScreenings = showEarlier ? selectedDay?.screenings : primeTimeScreenings;

    return (
        <div className="rialto-schedule">
            {/* Month label */}
            <h2 className="schedule-month">{dayjs(weekStart).format('MMMM YYYY')}</h2>

            {/* Days strip with prev/next nav buttons - single row */}
            <div className="rialto-schedule-days-row">
                <button
                    className="day-nav-btn day-nav-prev"
                    onClick={() => navigateWeek(-1)}
                    aria-label="Previous week"
                >
                    <svg className="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                    </svg>
                    <span className="nav-label">Previous</span>
                </button>
                <div className="rialto-schedule-days" ref={daysScrollRef}>
                    {weekData?.days?.map((day) => (
                        <button
                            key={day.date}
                            className={`rialto-schedule-day ${day.date === selectedDate ? 'active' : ''}`}
                            onClick={() => setSelectedDate(day.date)}
                        >
                            <div className="rialto-schedule-day-name">
                                {formatDayLabel(day.date)}
                            </div>
                            <div className="rialto-schedule-day-date">
                                {dayjs(day.date).format('D')}
                            </div>
                        </button>
                    ))}
                </div>
                <button
                    className="day-nav-btn day-nav-next"
                    onClick={() => navigateWeek(1)}
                    aria-label="Next week"
                >
                    <span className="nav-label">Next</span>
                    <svg className="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                    </svg>
                </button>
            </div>

            {/* Earlier Shows Visual Strip */}
            {earlierScreenings.length > 0 && (
                <div className="earlier-strip-container">
                    <div className="earlier-strip-header">
                        <span className="earlier-label">Earlier Today</span>
                        <button
                            className={`earlier-toggle-btn ${showEarlier ? 'expanded' : ''}`}
                            onClick={() => setShowEarlier(!showEarlier)}
                        >
                            {showEarlier ? 'Show prime time only' : `View all ${earlierScreenings.length} shows`}
                            <span className="toggle-icon">{showEarlier ? '▲' : '▼'}</span>
                        </button>
                    </div>
                    {!showEarlier && (
                        <div className="earlier-strip" ref={earlierStripRef}>
                            {earlierScreenings.map((screening) => {
                                const nowPlaying = isNowPlaying(screening, selectedDate);
                                return (
                                    <a
                                        key={screening.screening_id}
                                        href={screening.film.url}
                                        className={`earlier-strip-item${nowPlaying ? ' now-playing' : ''}`}
                                        title={screening.film.title}
                                        ref={nowPlaying ? nowPlayingRef : null}
                                        data-film-title={screening.film.title}
                                        data-film-year={screening.film.year || ''}
                                        data-film-genre={screening.film.genres?.[0] || ''}
                                        data-film-duration={screening.duration || ''}
                                        data-film-cert={screening.film.certification || ''}
                                        data-film-synopsis={screening.film.synopsis ? screening.film.synopsis.substring(0, 150) : ''}
                                        data-film-rating={screening.film.rating || ''}
                                        data-film-trailer={screening.film.trailer_url || screening.film.trailer_embed || ''}
                                    >
                                        {nowPlaying && <span className="now-playing-label">NOW PLAYING</span>}
                                        <div className="earlier-strip-poster">
                                            {screening.film.poster_thumb ? (
                                                <img src={screening.film.poster_thumb} alt={screening.film.title} />
                                            ) : (
                                                <div className="poster-placeholder-small" data-title={screening.film.title}>
                                                    <span className="placeholder-title">{screening.film.title}</span>
                                                </div>
                                            )}
                                        </div>
                                        <span className="earlier-strip-time">{formatTimeShort(screening.time)}</span>
                                    </a>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Prime Time Label */}
            {!showEarlier && primeTimeScreenings.length > 0 && (
                <div className="prime-time-label">
                    <span className="prime-icon">⭐</span>
                    <span>Prime Time Screenings</span>
                </div>
            )}

            <div className="rialto-schedule-list">
                {displayScreenings?.length > 0 ? (
                    displayScreenings.map((screening) => {
                        const isHighlighted = isHighlightedTime(screening.time);
                        const nowPlaying = isNowPlaying(screening, selectedDate);
                        const film = screening.film;
                        const hasTrailer = film.trailer_embed || film.trailer_url;
                        const hasRating = film.rating && parseFloat(film.rating) > 0;

                        return (
                            <div
                                key={screening.screening_id}
                                className={`rialto-schedule-item${isHighlighted ? ' highlighted' : ''}${nowPlaying ? ' now-playing' : ''}`}
                                ref={isHighlighted ? highlightRef : null}
                                onClick={(e) => handleRowClick(film.url, e)}
                                role="button"
                                tabIndex={0}
                            >
                                {/* Now Playing Badge */}
                                {nowPlaying && (
                                    <div className="now-playing-badge">NOW PLAYING</div>
                                )}
                                {/* Time Column */}
                                <div className="schedule-time-col">
                                    <span className="schedule-time">
                                        <span className="time-digits">{formatTime12hr(screening.time).time}</span>
                                        <span className="time-period">{formatTime12hr(screening.time).period}</span>
                                    </span>
                                    {isHighlighted && !nowPlaying && (
                                        <span className="featured-badge">FEATURED</span>
                                    )}
                                </div>

                                {/* Poster */}
                                <div className="schedule-poster">
                                    {film.poster_thumb ? (
                                        <img src={film.poster_thumb} alt={film.title} />
                                    ) : (
                                        <div className="poster-placeholder">
                                            <span>🎬</span>
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="schedule-content" data-time={`${formatTime12hr(screening.time).time} ${formatTime12hr(screening.time).period}`}>
                                    <h3 className="schedule-title">{film.title}</h3>
                                    <div className="schedule-details">
                                        <div className="schedule-meta-grid">
                                            {film.year && <span className="meta-year">{film.year}</span>}
                                            {film.genres?.length > 0 ? (
                                                <span className="meta-genre">{film.genres[0]}</span>
                                            ) : <span className="meta-genre">&nbsp;</span>}
                                            {screening.duration ? (
                                                <span className="meta-duration">{screening.duration} min</span>
                                            ) : <span className="meta-duration">&nbsp;</span>}
                                            {film.certification ? (
                                                <span className={`meta-cert cert-${film.certification.toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
                                                    {film.certification}
                                                </span>
                                            ) : <span className="meta-cert">&nbsp;</span>}
                                        </div>
                                        {film.synopsis && (
                                            <p className="schedule-synopsis">{film.synopsis.substring(0, 180)}...</p>
                                        )}
                                    </div>
                                </div>

                                {/* Ratings & Actions */}
                                <div className="schedule-ratings-actions">
                                    {/* Ratings */}
                                    <div className="schedule-ratings">
                                        {hasRating && (
                                            <>
                                                {film.tmdb_id && (
                                                    <a
                                                        href={`https://www.themoviedb.org/movie/${film.tmdb_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="rating-badge tmdb"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title="View on TMDB"
                                                    >
                                                        <span className="rating-source">TMDB</span>
                                                        <span className="rating-value">{parseFloat(film.rating).toFixed(1)}</span>
                                                    </a>
                                                )}
                                                {film.imdb_id && (
                                                    <a
                                                        href={`https://www.imdb.com/title/${film.imdb_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="rating-badge imdb"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title="View on IMDB"
                                                    >
                                                        <span className="rating-source">IMDb</span>
                                                    </a>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="schedule-actions">
                                        <button
                                            className="action-btn remind-btn"
                                            onClick={(e) => handleRemindMe(e, screening)}
                                            title="Remind Me"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                            </svg>
                                        </button>
                                        {hasTrailer && (
                                            <button
                                                className="action-btn trailer-btn"
                                                onClick={(e) => handleTrailer(e, film)}
                                                title="Watch Trailer"
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M8 5v14l11-7z"/>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Arrow indicator */}
                                <div className="schedule-arrow">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"/>
                                    </svg>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="schedule-empty">
                        <p>{t.noResults || 'No screenings scheduled for this time.'}</p>
                    </div>
                )}
            </div>

            {/* Trailer Modal */}
            {trailerModal && (
                <div className="trailer-modal-overlay" onClick={closeTrailerModal}>
                    <div className="trailer-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="trailer-modal-header">
                            <h3>{trailerModal.title}</h3>
                            <button className="trailer-modal-close" onClick={closeTrailerModal}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <div className="trailer-modal-content">
                            <iframe
                                src={trailerModal.embedUrl}
                                title={`${trailerModal.title} Trailer`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
